import React from 'react';
import { Truck, UserRound, Package, Bell, ChevronDown } from 'lucide-react';
import { useLang } from '@/lib/LangContext';

const HERO_IMG = 'https://images.pexels.com/photos/30769356/pexels-photo-30769356.jpeg?auto=compress&cs=tinysrgb&w=1600';

/**
 * Landing hero — fills the first viewport (minus header). Order per the
 * amendment brief: Title → Subtitle → 3 benefit chips → CTA buttons. A soft
 * "explore more" chevron at the bottom hints at scrollable content.
 */
export default function Hero({ onOpenSignup }) {
  const { t } = useLang();

  const scrollToHow = () => {
    const el = document.getElementById('partners') || document.getElementById('how');
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const scrollDownLabel = (t.nav?.simple?.scrollDown) || 'Explore more';

  return (
    <section
      id="home"
      className="relative pt-24 md:pt-24 pb-2 overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#F25C05]/10 blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-[#0A4D8C]/10 blur-3xl pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl w-full mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-10 items-center w-full">
          <div className="lg:col-span-7">
            <h1 className="font-display font-extrabold text-[#05223D] text-[28px] sm:text-[34px] lg:text-[40px] xl:text-[46px] leading-[1.08] tracking-[-0.02em]">
              {t.hero.titleA}
              <br />
              <span className="relative inline-block mt-1">
                <span className="relative z-10 text-[#0A4D8C]">{t.hero.titleB}</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 md:h-3.5 bg-[#F25C05]/30 -z-0 rounded-full" aria-hidden />
              </span>
            </h1>

            {/* Benefits grid — 2×2 so items align symmetrically.
                Lives directly beneath the title so the key value props land
                in the eye line before any paragraph copy. */}
            <ul
              data-testid="hero-stats"
              className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px] md:text-[14px] text-[#334155] max-w-2xl"
            >
              {[
                { Icon: Truck, label: t.hero.stat1 },
                { Icon: UserRound, label: t.hero.stat2 },
                { Icon: Package, label: t.hero.stat3 },
                { Icon: Bell, label: t.hero.stat4 },
              ].map(({ Icon, label }, i) => (
                <li key={label} className="flex items-center gap-2 min-w-0" data-testid={`hero-benefit-${i}`}>
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[#F25C05]/10 text-[#F25C05] flex items-center justify-center">
                    <Icon size={14} strokeWidth={2.2} />
                  </span>
                  <span className="font-semibold text-[#05223D] leading-tight">
                    {label}
                  </span>
                </li>
              ))}
            </ul>

            {/* Lead paragraph — moved BELOW the benefits per PO amendment so
                the "Register and have fun…" line reads as a closer, not an intro. */}
            <p
              data-testid="hero-sub"
              className="mt-7 md:mt-8 text-[#334155] text-[14px] md:text-[15px] lg:text-base leading-relaxed max-w-2xl"
            >
              {t.hero.sub}
            </p>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/3] max-h-[300px] lg:max-h-[320px] rounded-[2rem] overflow-hidden card-soft floaty mx-auto">
              <img
                src={HERO_IMG}
                alt="SmartPaw smart feeder with a dog"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05223D]/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll-down affordance — points to Partner Brands section below the fold */}
      <div className="relative flex justify-center mt-3">
        <button
          type="button"
          onClick={scrollToHow}
          aria-label={scrollDownLabel}
          data-testid="hero-scroll-down"
          className="inline-flex flex-col items-center gap-1 text-[#0A4D8C] group focus-visible:outline-none"
        >
          <span className="w-9 h-9 rounded-full bg-white/80 border border-[#0A4D8C]/15 shadow-sm flex items-center justify-center group-hover:bg-[#0A4D8C] group-hover:text-white group-hover:border-[#0A4D8C] transition-all">
            <ChevronDown size={16} className="animate-bounce" />
          </span>
        </button>
      </div>

    </section>
  );
}
